import { Component, OnInit, Input, Inject } from '@angular/core';
import { FirebaseTSAuth } from 'firebasets/firebasetsAuth/firebaseTSAuth';
import { FirebaseTSFirestore } from 'firebasets/firebasetsFirestore/firebaseTSFirestore';
import { UserData } from '../../models/user.model';
import { LucideAngularModule, Mic, Check, Play, Camera  } from 'lucide-angular';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA, MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { ChangeProfilePictureDialogComponent } from 'src/app/change-profile-picture-dialog/change-profile-picture-dialog.component';
import { FirebaseTSStorage } from 'firebasets/firebasetsStorage/firebaseTSStorage';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

@Component({
  selector: 'app-edit-profile-bottom-sheet',
  templateUrl: './edit-profile-bottom-sheet.component.html',
  styleUrls: ['./edit-profile-bottom-sheet.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatBottomSheetModule
  ]
})
export class EditProfileBottomSheetComponent implements OnInit {
  firestore: FirebaseTSFirestore;
  storage = new FirebaseTSStorage();
  auth: FirebaseTSAuth;
  showError = false;

  profileForm: FormGroup;
  profilePhotoUrl: string = '';

  recordingStates: { [key: string]: boolean } = {};
  audioBlobs: { [key: string]: Blob | null } = {};
  audioURLs: { [key: string]: string | null } = {};
  mediaRecorders: { [key: string]: MediaRecorder | null } = {};
  audioPlayers: { [key: string]: HTMLAudioElement | null } = {};
  isPlaying: { [key: string]: boolean } = {};
  recordedAudioURLs: { [key: string]: string } = {};

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private bottomSheetRef: MatBottomSheetRef<EditProfileBottomSheetComponent>,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: any
  ) {
    this.firestore = new FirebaseTSFirestore();
    this.auth = new FirebaseTSAuth();

    this.profileForm = this.fb.group({
      name: [data.publicName || '', [Validators.required, Validators.minLength(2)]],
      profession: [data.profession || '', [Validators.required]],
      city: [data.ville || '', [Validators.required]]
    });

    this.profilePhotoUrl = data.profilePhotoUrl || '';
  }

  ngOnInit(): void {
    // Initialization code...
  }

  onClose(): void {
    this.bottomSheetRef.dismiss();
  }

  onSave(): void {
    if (this.profileForm.valid) {
      const formData = this.profileForm.value;
      const userId = this.auth.getAuth().currentUser?.uid;
      
      if (userId) {
        this.firestore.update({
          path: ["Users", userId],
          data: {
            publicName: formData.name,
            profession: formData.profession,
            ville: formData.city,
            profilePhotoUrl: this.profilePhotoUrl
          }
        }).then(() => {
          this.snackBar.open('Profil mis à jour avec succès', 'Fermer', {
            duration: 3000
          });
          this.bottomSheetRef.dismiss(true);
        });
      }
    }
  }

  // Démarrer/Arrêter l'enregistrement
  toggleRecording(field: string) {
    if (this.recordingStates[field]) {
      this.stopRecording(field);
    } else {
      this.startRecording(field);
    }
  }

  async startRecording(field: string) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];

      recorder.ondataavailable = (event) => chunks.push(event.data);
      recorder.onstop = () => {
        this.audioBlobs[field] = new Blob(chunks, { type: 'audio/wav' });
        this.audioURLs[field] = URL.createObjectURL(this.audioBlobs[field]);
      };

      recorder.start();
      this.mediaRecorders[field] = recorder;
      this.recordingStates[field] = true;
    } catch (err) {
      console.error('Erreur d\'enregistrement :', err);
    }
  }

  stopRecording(field: string) {
    if (this.mediaRecorders[field]) {
      this.mediaRecorders[field].stop();
      this.recordingStates[field] = false;
    }
  }

  // Jouer/Arrêter l'audio
  togglePlayback(field: string) {

    if (!this.audioURLs[field]) {
      console.warn('Aucun enregistrement trouvé pour', field);
      return;
    }

    if (!this.audioPlayers[field]) {
      this.audioPlayers[field] = new Audio(this.audioURLs[field]);
      this.audioPlayers[field].onended = () => {
        this.isPlaying[field] = false;
      };
    }

    if (this.isPlaying[field]) {
      this.audioPlayers[field].pause();
      this.audioPlayers[field].currentTime = 0;
      this.isPlaying[field] = false;
    } else {
      this.audioPlayers[field].play();
      this.isPlaying[field] = true;
    }
  }

  // Méthode pour enregistrer un nouvel audio depuis le microphone
  saveRecordedAudio(soundKey: string, blob: Blob) {
    if (this.recordedAudioURLs[soundKey]) {
      URL.revokeObjectURL(this.recordedAudioURLs[soundKey]); // Nettoie l'ancien fichier
    }
    this.recordedAudioURLs[soundKey] = URL.createObjectURL(blob);
  }

  isFieldValid(fieldName: string): boolean {
    const control = this.profileForm.get(fieldName);
    return control?.valid && control?.touched;
  }

  onSubmit(): void {
    if (this.profileForm.valid) {
      console.log('Form Data:', this.profileForm.value);
    }
  }

  onChangeProfilePicture() {
    const dialogRef = this.dialog.open(ChangeProfilePictureDialogComponent);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.uploadProfilePicture(result);
      } 
    }); 
  }

  uploadProfilePicture(image: string | ArrayBuffer) {
    // Convertir l'image en Blob
    let imageBlob: Blob;
    
    if (typeof image === 'string') {
      // Si c'est une string base64, la convertir en Blob
      const byteString = atob(image.split(',')[1]);
      const mimeString = image.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      imageBlob = new Blob([ab], { type: mimeString });
    } else {
      // Si c'est un ArrayBuffer, le convertir directement en Blob
      imageBlob = new Blob([image], { type: 'image/jpeg' });
    }

    const userId = this.auth.getAuth().currentUser?.uid;
    if (userId) {
      const path = `Users/${userId}/profile_photo`;
      this.storage.upload(
        {
          uploadName: "Upload Profile Photo",
          path: [path],
          data: {
            data: imageBlob,
            metadata: { contentType: 'image/jpeg' }
          },
          onComplete: (downloadURL) => {
            console.log("Profile photo uploaded!", downloadURL);
            this.profilePhotoUrl = downloadURL;
            this.firestore.update(
              {
                path: ["Users", userId],
                data: {
                  profilePhotoUrl: downloadURL
                }
              }
            );
          },
          onFail: (error) => {
            console.error("Upload failed", error);
          }
        }
      );
    }
  }

  onChangeProfilePhoto(event: any): void {
    const file = event.target.files[0];
    if (file) {
      const uploadMetadata: firebase.default.storage.UploadMetadata = {
        contentType: file.type // Type de contenu pour l'image (par exemple, 'image/jpeg')
      };
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.profilePhotoUrl = e.target.result;
        const userId = this.auth.getAuth().currentUser?.uid;
        if (userId) {
          const path = `Users/${userId}/profile_photo`;
          this.storage.upload(
            {
              uploadName: "Upload Profile Photo",
              path: [path],
              data: {
                data: file,
                metadata: uploadMetadata
              },
              onComplete: (downloadURL) => {
                console.log("Profile photo uploaded!", downloadURL);
                this.profilePhotoUrl = downloadURL;
                this.firestore.update(
                  {
                    path: ["Users", userId],
                    data: {
                      profilePhotoUrl: downloadURL
                    }
                  }
                );
              },
              onFail: (error) => {
                console.error("Upload failed", error);
              }
            }
          );
        }
      };
      reader.readAsDataURL(file);
    }
  }

  onContinueClick2() {
    console.log("Continue button clicked");
    // Votre logique ici
  }

  onContinueClick(
    nameInput: HTMLInputElement,
    villeInput: HTMLInputElement,
    descriptionInput: HTMLTextAreaElement
  ) {
    if (nameInput.value && villeInput.value && descriptionInput.value) {
      this.showError = false;
      console.log('Formulaire validé:', {
        nom: nameInput.value,
        ville: villeInput.value,
        description: descriptionInput.value
      });

      let name = nameInput.value;
      let ville = villeInput.value;
      let description = descriptionInput.value;
      const userId = this.auth.getAuth().currentUser?.uid;

      if (userId) {
        const audioUploadPromises = Object.keys(this.audioBlobs).map(async (field) => {
          const blob = this.audioBlobs[field];
          if (blob) {
            const path = `Users/${userId}/audio/${field}.wav`;
            return this.storage.upload({
              uploadName: "Upload Audio",
              path: [path],
              data: {
                data: blob,
                metadata: { contentType: blob.type }
              }
            }).then((downloadURL) => {
              console.log(`Audio ${field} uploaded!`, downloadURL);
              return { field, downloadURL };
            }).catch((error) => {
              console.error(`Upload failed for ${field}`, error);
              return null;
            });
          }
          return null;
        });

        Promise.all(audioUploadPromises).then(async (audioURLs) => {
          const audioData = audioURLs.reduce((acc, audio) => {
            if (audio) {
              acc[audio.field] = audio.downloadURL;
            }
            return acc;
          }, {});

          const updateData: any = {
            publicName: name,
            ville: ville,
            description: description,
            profilePhotoUrl: this.profilePhotoUrl,
            coverPhotoUrl: "",
            followers: [],
            following: [],
            uid: userId
          };

          Object.keys(audioData).forEach((field) => {
            updateData[field + "audioUrl"] = audioData[field];
          });

          const userDoc = await this.firestore.getDocument({
            path: ["Users", userId]
          });

          if (userDoc.exists) {
            this.firestore.update({
              path: ["Users", userId],
              data: updateData,
              onComplete: () => {
                alert("Votre profil a bien été mis à jour!");
                nameInput.value = "";
                descriptionInput.value = "";
              },
              onFail: (err) => {
                console.error("Erreur lors de la mise à jour du profil:", err);
              }
            });
          } else {
            this.firestore.create({
              path: ["Users", userId],
              data: updateData,
              onComplete: (docId) => {
                alert("Votre profil a bien été créé!");
                nameInput.value = "";
                descriptionInput.value = "";
              },
              onFail: (err) => {
                console.error("Erreur lors de la création du profil:", err);
              }
            });
          }
        });
      }
    } else {
      this.showError = true;
    }
  }
}
